import React, { useState } from "react";
import Button from "./Button";
import ProgressBar from "./ProgressBar";
import { useStateContext } from "../contexts/ContextProvider";
import { apiUploadUserId } from "../services/apiService";
import { MdVerifiedUser } from "react-icons/md";
import { HiOutlineIdentification } from "react-icons/hi2";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { storage } from "../services/firebaseConfig";
import { getRandomString } from "../helpers/randomString";

const initialErrorState = {
  fileError: "",
};

const VerifyIdModal = () => {
  const { user, setUser, handleModal } = useStateContext();
  const [identification, setIdentification] = useState("");
  const [fileSizeError, setFileSizeError] = useState(false);
  const [fileTypeError, setFileTypeError] = useState(false);
  const [error, setError] = useState(initialErrorState);
  const [page, setPage] = useState(1);
  const pages = 3;
  const maxFileSizeMB = 5; // max file size in Megabytes

  const nextPage = () => {
    setPage(page + 1);
  };

  const prevPage = () => {
    setPage(page - 1);
    setIdentification("");
  };

  const handleUpload = async (event) => {
    const file = event.target.files[0];
    setFileSizeError(false);
    setFileTypeError(false);
    if (file.size > maxFileSizeMB * 1024 * 1024) {
      setFileSizeError(true);
      document.getElementById("image").value = "";
      return;
    } else if (file.type.match("image/*") || file.type === "application/pdf") {
      // define storge location, and new file name
      const imageRef = ref(
        storage,
        `identifications/${user.uuid}/${getRandomString(20)}.${
          file.name.split(".")[1]
        }`
      );

      // upload file to cloud
      await uploadBytes(imageRef, file).then(async (snapshot) => {
        await getDownloadURL(snapshot.ref).then((url) => {
          // collect url, and add to array
          console.log("uploaded file");
          saveFile(url);
        });
      });
      setIdentification(file);
      return;
    }
    setFileTypeError(true);
    document.getElementById("image").value = "";
  };

  const handleNext = () => {
    if (hasErrors()) {
      return;
    } else {
      nextPage();
    }
  };

  const saveFile = async (url) => {
    console.log("save file");
    const oldFile = user.identification;
    const response = await apiUploadUserId(user.id, { identification: url });
    if (response.hasError) {
      //TODO: ADD Error Message
      return;
    } else {
      console.log("save user", response.data);
      setUser(response.data);
      if (oldFile) removeOldFile(oldFile);
    }
  };

  const removeOldFile = (url) => {
    console.log(url);
    // delete image from image server
    const imageRef = ref(storage, url);
    getDownloadURL(imageRef)
      .then(() => {
        // File exists, proceed with deletion
        deleteObject(imageRef)
          .then(() => {
            console.log("File deleted successfully!");
          })
          .catch((error) => {
            console.error("Error deleting file:", error);
          });
      })
      .catch((error) => {
        // File does not exist or other error occurred
        if (error.code === "storage/object-not-found") {
          console.log("File does not exist, no need to delete.");
        } else {
          console.error("Error checking file existence:", error);
        }
      });
  };

  const hasErrors = () => {
    if (
      page === 2 &&
      (identification === "" || fileSizeError || fileTypeError)
    ) {
      activateError("fileError");
      return true;
    } else {
      return false;
    }
  };

  const activateError = (item) => {
    setError((prevState) => ({
      ...prevState,
      [item]: "animate-shake text-red-600 border-red-600",
    }));
    setTimeout(() => {
      setError((prevState) => ({
        ...prevState,
        [item]: "",
      }));
    }, 500);
    clearTimeout();
  };

  return (
    <div className="w-screen h-screen fixed top-0 left-0 right-0 bg-white mt-20 z-10">
      <ProgressBar pages={pages} currentPage={page} />
      {page === 1 && (
        <div className="flex justify-center mt-20 px-10">
          <div className="border-gray-500 border-2 rounded-3xl bg-gray-300 p-5">
            <p className="text-center">Please upload a government ID.</p>
            <ul className="list-disc mt-5 pl-10">
              <li>Driver's License</li>
              <li>State ID</li>
              <li>Passport</li>
              <li>Passport Card</li>
              <li>Green Card</li>
            </ul>
            <p className="text-center mt-5">Click "Next" to proceed.</p>
          </div>
        </div>
      )}
      {page === 2 && (
        <>
          <div
            className={`flex justify-center text-2xl mt-10 ${error.fileError}`}
          >
            Upload the front of your ID
          </div>
          <p className="flex justify-center text-snappily-orange text-sm">
            (Name must match profile name)
          </p>
          <div className="flex justify-center">
            {!user.identification && (
              <HiOutlineIdentification className="text-9xl" />
            )}
            {user.identification && (
              <div className="h-28 bg-gray-300 mt-4">
                <img
                  src={
                    user.identification instanceof Blob
                      ? URL.createObjectURL(user.identification)
                      : user.identification
                  }
                  alt={`${user.firstName}`}
                  className="h-28  object-cover drop-shadow-xl cursor-pointer"
                  // onClick={handleClick}
                />
              </div>
            )}
          </div>
          <div className="grid justify-items-center mt-5">
            <input
              id="image"
              name="image"
              type="file"
              accept="image/*, application/pdf"
              onChange={handleUpload}
              className={`block w-72 border-gray-300 border rounded-lg p-1 ${error.fileError}`}
            />
            {fileSizeError && (
              <div className="text-red-500">File is too large!</div>
            )}
            {fileTypeError && (
              <div className="text-red-500">Images or PDFs only!</div>
            )}
          </div>
        </>
      )}
      {page === pages && (
        <>
          <div className="grid justify-items-center mt-10">
            <div className="text-xl mt-10">Your ID is pending approval</div>
            <MdVerifiedUser className="text-8xl text-blue-500 mt-5" />
          </div>
        </>
      )}
      <div className="flex justify-center fixed bottom-0 w-full my-10">
        {page < pages && (
          <Button
            background="bg-slate-400"
            text="text-white"
            opacity="bg-opacity-100"
            title={page === 1 ? "Cancel" : "Back"}
            height="h-10"
            width="w-24"
            borderColor="border-none"
            borderHover="hover:border-snappily-orange"
            customFunc={page === 1 ? () => handleModal("verifyId") : prevPage}
          />
        )}
        <Button
          background="bg-snappily-orange"
          text="text-white"
          opacity="bg-opacity-100"
          title={page === 3 ? "Done" : "Next"}
          height="h-10"
          width="w-24"
          borderColor="border-none"
          borderHover="hover:border-snappily-orange"
          customFunc={
            page === 1
              ? nextPage
              : page === 2
              ? handleNext
              : () => handleModal("verifyId")
          }
        />
      </div>
    </div>
  );
};

export default VerifyIdModal;
